import {ArrowDownOutlined, ArrowUpOutlined, MessageOutlined} from '@ant-design/icons';
import {Button, Flex, Select, Tag, theme, Typography} from 'antd';
import CommentsDialog from 'Pages/SRDashboard/components/ResultsTable/CommentsDialog';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {NoSearchResults} from 'Components';
import {MEMBER_SEARCH} from 'Pages/SearchMember/searchConstants';
import {searchSRByID} from 'Redux/Slices/selectedSR';
import {SortOrder} from 'Utils/enums';
import {FORMAT_TYPE} from 'Utils/constants/DateFormat';
import {dateConversionIsoToLocal} from 'Utils/helpers/DateValidation';
import {sortTable} from 'Utils/helpers/SRListHelpers';

const {Text} = Typography;

// The old SR table's sortable columns.
const SORT_OPTIONS = [
  {label: 'Created Date', value: 'createdOn'},
  {label: 'Modified Date', value: 'modifiedOn'},
  {label: 'SR#', value: 'id'},
  {label: 'SR Type', value: 'type'},
  {label: 'Status', value: 'status'},
  {label: 'Service', value: 'serviceType'},
  {label: 'Summary', value: 'summary'},
];

// SR status strings are free-form (see Utils/constants/genericConstants
// statusList) — this is a pragmatic keyword heuristic for the list-card
// badge color, not an exhaustive/authoritative status taxonomy.
const statusTagColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (
    ['cancelled', 'denied', 'rejected', 'declined', 'failed', 'escalat'].some((k) => s.includes(k))
  ) {
    return 'error';
  }
  if (
    [
      'pending',
      'assessing',
      'requested',
      'quoted',
      'missing',
      'fee not paid',
      'review',
      'hold',
    ].some((k) => s.includes(k))
  ) {
    return 'warning';
  }
  if (
    ['open', 'approved', 'confirmed', 'completed', 'supplied', 'passed'].some((k) => s.includes(k))
  ) {
    return 'success';
  }
  return 'default';
};

// `.list-card` pattern from the AST Console Revamp mockup's `#tab-sr`: a
// stacked list of bordered rows (title + meta on the left, status badge +
// action on the right) — replaces the previous data-table rendering.
const SRListView = (props) => {
  const dispatch = useDispatch();
  const {SRListData} = props;
  const {token} = theme.useToken();
  const [currentSortCriteria, setCriteriaSort] = useState('createdOn');
  const [sortOrder, setSortOrder] = useState(SortOrder.ASCENDING);
  const [tableData, setTableData] = useState<any[]>([]);
  const tenantConfig = useSelector((state: any) => state?.login?.tenantConfig);
  const [noResults, setNoResults] = useState(false);
  const [openCommentsDialog, setOpenCommentsDialog] = useState(false);
  const [commentData, setCommentData] = useState('');

  useEffect(() => {
    if (SRListData && SRListData?.length > 0) {
      setNoResults(false);
      parseUnfilteredData(SRListData);
    } else {
      setNoResults(true);
    }
  }, [SRListData]);

  const parseUnfilteredData = (data) => {
    const temp: any = [];
    (data || []).forEach((item: any) => {
      temp.push({
        type: item?.type,
        subType: item?.subType,
        id: item?.id,
        status: item?.status,
        serviceType: item?.additionalData?.service,
        summary: item?.summary,
        createdOn: item?.audit?.createdOn
          ? dateConversionIsoToLocal(
              item.audit?.createdOn,
              FORMAT_TYPE.DATE_TIME,
              '',
              tenantConfig.locale,
            )
          : '',
        modifiedOn: item?.audit?.modifiedOn
          ? dateConversionIsoToLocal(
              item.audit?.modifiedOn,
              FORMAT_TYPE.DATE_TIME,
              '',
              tenantConfig.locale,
            )
          : '',
        srData: item,
      });
    });
    let resp = sortTable(temp, 'createdOn', 'createdOn', SortOrder.ASCENDING, tenantConfig.locale);
    setCriteriaSort(resp.currentSortCriteria);
    setTableData(resp.tableData);
    setSortOrder(resp.newSortOrder);
  };

  // Same behaviour as the old table's column-header sort: picking a new field
  // sorts by it, picking the current field again flips the order.
  const onSortChange = (sortCriteria: string) => {
    const resp = sortTable(
      tableData,
      sortCriteria,
      currentSortCriteria,
      sortOrder,
      tenantConfig.locale,
    );
    setCriteriaSort(resp.currentSortCriteria);
    setTableData(resp.tableData);
    setSortOrder(resp.newSortOrder);
  };

  const onRowClick = async (rowData) => {
    let {srData} = rowData;
    let srPayload = {
      id: srData.id,
    };
    let response = await dispatch(searchSRByID(srPayload));
    if (response) {
      props.handleSRRowClick();
    }
  };

  const showComments = (e: React.MouseEvent, row: any) => {
    e.stopPropagation();
    setCommentData(row?.srData?.additionalData?.comments);
    setOpenCommentsDialog(true);
  };

  return (
    <div>
      <CommentsDialog
        openCommentsDialog={openCommentsDialog}
        setOpenCommentsDialog={setOpenCommentsDialog}
        comments={commentData}
      />
      {!noResults && tableData.length > 1 ? (
        <Flex justify="flex-start" align="center" gap={8} style={{marginBottom: 10}}>
          <Text style={{fontSize: 12, color: token.colorTextSecondary}}>Sort by</Text>
          <Select
            size="small"
            style={{width: 150}}
            value={currentSortCriteria}
            options={SORT_OPTIONS}
            // onSelect (not onChange) so re-picking the current field flips the order.
            onSelect={onSortChange}
          />
          <Button
            size="small"
            type="text"
            aria-label="Toggle sort order"
            icon={sortOrder === SortOrder.ASCENDING ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            onClick={() => onSortChange(currentSortCriteria)}
          />
        </Flex>
      ) : null}
      {noResults ? (
        <NoSearchResults CSTexts={MEMBER_SEARCH.no_search_result} />
      ) : (
        tableData.map((row) => (
          <Flex
            key={row.id}
            align="center"
            justify="space-between"
            gap={16}
            onClick={() => onRowClick(row)}
            style={{
              background: token.colorBgContainer,
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: token.borderRadiusLG,
              padding: '14px 16px',
              marginBottom: 10,
              cursor: 'pointer',
            }}>
            <Flex vertical gap={4} style={{minWidth: 0}}>
              <Text strong style={{fontSize: 13}}>
                {row.id} · {row.type}
              </Text>
              <Text style={{fontSize: 12, color: token.colorTextSecondary}} ellipsis>
                Created {row.createdOn}
                {row.modifiedOn ? ` · Modified ${row.modifiedOn}` : ''}
                {row.serviceType ? ` · ${row.serviceType}` : ''}
                {row.summary ? ` · ${row.summary}` : ''}
              </Text>
              {row.subType ? (
                <Text style={{fontSize: 12, color: token.colorTextSecondary}} ellipsis>
                  Sub Type: {row.subType}
                </Text>
              ) : null}
            </Flex>
            <Flex align="center" gap={14} style={{flexShrink: 0}}>
              <Tag color={statusTagColor(row.status)} style={{margin: 0}}>
                {row.status}
              </Tag>
              <Button
                type="text"
                size="small"
                icon={<MessageOutlined />}
                onClick={(e) => showComments(e, row)}
              />
            </Flex>
          </Flex>
        ))
      )}
    </div>
  );
};

export default SRListView;
